import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, AlertTriangle, Send } from 'lucide-react'
import { reportService, ReportReason } from '../../services/reportService'
import { useAuth } from '../../contexts/AuthContext'

interface ReportModalProps {
    isOpen: boolean
    onClose: () => void
    targetId: string
    targetType: 'event' | 'user'
}

const REASONS: { value: ReportReason; label: string }[] = [
    { value: 'spam', label: 'Spam / Reklam' },
    { value: 'illegal', label: 'Yasadışı İçerik' },
    { value: 'nudity', label: 'Çıplaklık / Pornografi' },
    { value: 'profanity', label: 'Hakaret / Küfür' },
    { value: 'impersonation', label: 'Taklit / Sahte Hesap' },
    { value: 'other', label: 'Diğer' },
]

export function ReportModal({ isOpen, onClose, targetId, targetType }: ReportModalProps) {
    const { sk } = useAuth()
    const [selectedReason, setSelectedReason] = useState<ReportReason>('spam')
    const [comment, setComment] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit = async () => {
        if (isSubmitting) return
        setIsSubmitting(true)

        try {
            await reportService.sendReport(
                targetId,
                targetType,
                selectedReason,
                comment,
                sk // Pass local key if available, service handles window.nostr priority
            )
            alert('Bildiriminiz gönderildi. Teşekkürler!')
            onClose()
            setComment('')
            setSelectedReason('spam')
        } catch (error: any) {
            console.error('Report failed:', error)
            alert('Bildirim gönderilemedi: ' + error.message)
        } finally {
            setIsSubmitting(false)
        }
    }

    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white dark:bg-surface-900 rounded-2xl w-full max-w-md shadow-2xl border border-surface-200 dark:border-surface-700 overflow-hidden"
                >
                    <div className="p-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
                        <h3 className="font-poppins font-bold text-lg text-gray-900 dark:text-white flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Bildir
                        </h3>
                        <button
                            onClick={onClose}
                            className="p-1 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    <div className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Bildirim Nedeni
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {REASONS.map((reason) => (
                                    <button
                                        key={reason.value}
                                        onClick={() => setSelectedReason(reason.value)}
                                        className={`p-2 rounded-xl text-xs font-medium transition-all border ${selectedReason === reason.value
                                            ? 'bg-red-50 dark:bg-red-900/20 border-red-500 text-red-600 dark:text-red-400'
                                            : 'bg-surface-50 dark:bg-surface-800 border-surface-200 dark:border-surface-700 text-gray-600 dark:text-gray-400 hover:border-red-200 dark:hover:border-red-800'
                                            }`}
                                    >
                                        {reason.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Ek Açıklama (Opsiyonel)
                            </label>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Detay ekleyebilirsiniz..."
                                className="w-full h-24 p-3 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-mavi-500 text-sm resize-none"
                            />
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Gönderiliyor...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Bildirimi Gönder
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
